import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProviderPaymentSummary } from "../slice/providerPanelDashboardSlice";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function ProviderDashboard() {
    const dispatch = useDispatch();
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const providerToken = localStorage.getItem("provider_access_token");
    const { revenueSplit, topServices, monthServices, loading, error, aiSummary } = useSelector((state) => state.providerDashboard);

    useEffect(() => {
        dispatch(getProviderPaymentSummary({ token: providerToken, year: selectedYear }));
    }, [dispatch]);


    if (loading) 
        return (
            <div className="col-md-8 col-xl-9 mt-4 mt-md-0 d-flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}>
                <div className="text-center">
                <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading...</p>
                </div>
            </div>
        );


    if (error) return <div className="text-danger text-center mt-4">{error}</div>

    const handleYearChange = (e) => {
        const year = e.target.value;
        setSelectedYear(year);
        dispatch(getProviderPaymentSummary({ token: providerToken, year }));
    };

    // List of 12 months (YYYY-MM format)
    const currentYear = new Date().getFullYear();
    const allMonths = Array.from({ length: 12 }, (_, i) => {
        const month = (i + 1).toString().padStart(2, "0");
        return `${selectedYear}-${month}`;  // ✅ use selected year
    });

    // Group data by service
const serviceMap = {};

monthServices.forEach((entry) => {
  const { service_name, payment_month, total_amount } = entry;
  if (!serviceMap[service_name]) {
    serviceMap[service_name] = {};
  }
  serviceMap[service_name][payment_month] =
    (serviceMap[service_name][payment_month] || 0) + parseFloat(total_amount);
});

// Helper to generate random colors
const getRandomColor = () =>
  `hsl(${Math.floor(Math.random() * 360)}, 70%, 50%)`;

// Create one dataset per service
const lineDatasets = Object.keys(serviceMap).map((serviceName) => {
  const dataPoints = allMonths.map((month) => serviceMap[serviceName][month] || 0);

  return {
    label: serviceName,
    data: dataPoints,
    borderColor: getRandomColor(),
    backgroundColor: "transparent",
    borderWidth: 3,
    tension: 0.3,
    pointRadius: 4,
    pointHoverRadius: 6,
  };
});


    const years = [];
    for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
    }

    // Make a map from API data for quick lookup
    const monthMap = {};
    monthServices.forEach((m) => {
        const key = m.payment_month;
        const amount = parseFloat(m.total_amount);

        if (!monthMap[key]) {
            monthMap[key] = amount;
        } else {
            monthMap[key] += amount; // ✅ Add instead of replace
        }
    });

    // Build data array for all months (default 0)
    const monthLabels = allMonths;
    const monthAmounts = allMonths.map((m) => monthMap[m] || 0);

    const barData = {
        labels: monthLabels,
        datasets: [
            {
            label: "Monthly Revenue ($)",
            data: monthAmounts,
            backgroundColor: "rgba(54, 162, 235, 0.6)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 2,
            borderRadius: 6,
            },
        ],
    };

    // const barOptions = {
    //     responsive: true,
    //     plugins: {
    //         legend: { position: "top" },
    //         title: { display: true, text: "Revenue by Month" },
    //     },
    // };

    const barOptions = {
  responsive: true,
  plugins: {
    legend: { position: "top" },
    title: { display: true, text: "Revenue by Month" },
    tooltip: {
      callbacks: {
        label: function (context) {
          const month = context.label; // YYYY-MM
          
          // Filter services for this month
          const monthDetails = monthServices.filter(
            (m) => m.payment_month === month
          );

          if (monthDetails.length === 0) {
            return `No service data`;
          }

          // Build tooltip lines
          return monthDetails.map((m) => {
            return `${m.service_name}: $${m.total_amount} (${m.total_payments})`;
          });
        }
      }
    }
  }
};

const lineData = {
  labels: allMonths,
  datasets: lineDatasets,
};


const lineOptions = {
  responsive: true,
  plugins: {
    legend: { position: "top" },
    title: { display: true, text: "Revenue Trend (Line Chart)" },
  },
  scales: {
    y: {
      beginAtZero: true,
      title: { display: true, text: "Revenue ($)" },
    },
    x: {
      title: { display: true, text: "Month" },
    },
  },
};


    return (
        <div className="col-md-8 col-xl-9 mt-4 mt-md-0">
            <div className="ps-2 ps-lg-4">
                <h4 className="font-weight-bold text-primary font-size-40 mb-4 pb-1 line-hight-2 border-bottom pb-3 montserrat">Dashboard</h4>

                <div className="row">
                    {revenueSplit.map((item, idx) => (
                        <React.Fragment key={idx}>
        
                            {/* Total Revenue */}
                            <div className="col-xl-4 col-md-6 mb-4">
                                <div className="card shadow-sm border-2 h-100">
                                <div className="card-body d-flex justify-content-between align-items-center">
                                    <div>
                                    <h6 className="text-primary text-uppercase fw-bold mb-1">Total Revenue</h6>
                                    <h4 className="fw-bold mb-0">${item.total_revenue}</h4>
                                    </div>
                                    <i className="bi bi-bar-chart fs-1 text-secondary opacity-50"></i>
                                </div>
                                </div>
                            </div>

                            {/* Provider Share */}
                            <div className="col-xl-4 col-md-6 mb-4">
                                <div className="card shadow-sm border-2 h-100">
                                <div className="card-body d-flex justify-content-between align-items-center">
                                    <div>
                                    <h6 className="text-success text-uppercase fw-bold mb-1">Provider Share</h6>
                                    <h4 className="fw-bold mb-0">${item.provider_share}</h4>
                                    </div>
                                    <i className="bi bi-wallet2 fs-1 text-secondary opacity-50"></i>
                                </div>
                                </div>
                            </div>

                            {/* Admin Share */}
                            <div className="col-xl-4 col-md-6 mb-4">
                                <div className="card shadow-sm border-2 h-100">
                                <div className="card-body d-flex justify-content-between align-items-center">
                                    <div>
                                    <h6 className="text-warning text-uppercase fw-bold mb-1">Admin Share</h6>
                                    <h4 className="fw-bold mb-0">${item.admin_share}</h4>
                                    </div>
                                    <i className="bi bi-person-gear fs-1 text-secondary opacity-50"></i>
                                </div>
                                </div>
                            </div>

                        </React.Fragment>
                    ))}
                </div>


                {/* Top Services Table */}
                <div className="card shadow-sm mb-4">
                    <div className="card-body">
                    <h5>Top Services</h5>
                    <table className="table table-bordered mt-3">
                        <thead>
                        <tr>
                            <th>Service Name</th>
                            <th>Total Amount</th>
                            <th>Subscriptions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {topServices.length === 0 ? (
                                <tr>
                                <td colSpan="4" className="text-center">No data found</td>
                                </tr>
                            ) : topServices.map((srv, idx) => (
                            <tr key={idx}>
                            <td>{srv.service_name}</td>
                            <td>${srv.total_amount}</td>
                            <td>{srv.total_payments}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                </div>

                <div className="mb-3 d-flex justify-content-start align-items-center gap-2">
                    <h5 className="mb-0 me-2">Selected Year:</h5>

                    <select 
                        className="form-select w-auto"
                        value={selectedYear}
                        onChange={handleYearChange}
                    >
                        {years.map((y) => (
                        <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>

               <div className="card p-3 shadow-sm border-success mt-4 mb-4">
                    <h5>🤖 AI Insights</h5>
                    <div className="row g-4">
                        <div className="col-md-4"><div className="card p-3 shadow-sm"><h6>Total Revenue</h6><p>$ {aiSummary.total_revenue}</p></div></div>
                        <div className="col-md-4"><div className="card p-3 shadow-sm"><h6>Average Monthly Revenue</h6><p>$ {aiSummary.average_monthly_revenue}</p></div></div>
                        <div className="col-md-4"><div className="card p-3 shadow-sm"><h6>Top Service Percentage</h6><p>{aiSummary.top_service_percentage} %</p></div></div>
                    </div>

                    <div className="row mt-4 g-4">
                        <div className="col-md-6"><div className="card p-3 shadow-sm"><h5>Monthly Trend</h5><p>{aiSummary.monthly_trend}</p></div></div>
                        <div className="col-md-6"><div className="card p-3 shadow-sm"><h5>Monthly Growth Trend</h5><p>{aiSummary.monthly_growth_trend}</p></div></div>
                    </div>

                    <div className="row mt-4 g-4">
                        <div className="col-md-6"><div className="card p-3 shadow-sm"><h5>Engagement Level</h5><p>{aiSummary.engagement_level}</p></div></div>
                        <div className="col-md-6"><div className="card p-3 shadow-sm"><h5>High Frequency Low Revenue services</h5><p>{aiSummary.high_frequency_low_revenue_services}</p></div></div>
                    </div>

                    <div className="card p-3 shadow-sm border-success mt-4"><h5>Main Trend</h5><p>{aiSummary.main_trend}</p></div>
                    <div className="card p-3 shadow-sm border-success mt-4"><h5>Recommendations</h5><p>{aiSummary.recommendations}</p></div>
                </div>

                {/* Monthly Revenue Table */}
                <div className="card shadow-sm mb-4">
                    <div className="card-body">
                        <div className="d-flex justify-content-between">
                            <h5>Monthly Service Summary</h5>
                            <span>
                                <strong>Year: </strong>
                            {selectedYear}
                            </span>
                        </div>
                        <table className="table table-bordered mt-3">
                            <thead>
                                <tr>
                                    <th>Month</th>
                                    <th>Service Name</th>
                                    <th>Total Amount</th>
                                    <th>Subscriptions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthServices.length === 0 ? (
                                        <tr>
                                        <td colSpan="5" className="text-center">No data found</td>
                                        </tr>
                                    ) : monthServices.map((m, idx) => (
                                    <tr key={idx}>
                                    <td>{m.payment_month}</td>
                                    <td>{m.service_name}</td>
                                    <td>${m.total_amount}</td>
                                    <td>{m.total_payments}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                {/* Bar Chart for Monthly Revenue */}
                <div className="card shadow-sm mb-4">
                    <div className="card-body">
                        <div className="d-flex justify-content-between">
                            <h5 className="mb-3">Revenue Chart (Monthly)</h5>
                            <span>
                                <strong>Year: </strong>
                                {selectedYear}
                            </span>
                        </div>
                        {/* Chart */}
                        <Bar data={barData} options={barOptions} height={100} />
                    </div>
                </div>

                {/* Line Chart for Monthly Revenue Trend */}
                <div className="card shadow-sm mb-4">
                    <div className="card-body">
                        <div className="d-flex justify-content-between">
                            <h5 className="mb-3">Revenue Trend by Service (Line)</h5>
                            <span>
                                <strong>Year: </strong>
                                {selectedYear}
                            </span>
                        </div>
                        <Line data={lineData} options={lineOptions} height={100} />
                    </div>
                </div>
            </div>
        </div>
    );
}
